import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import type { ChartOptions } from "chart.js";
import { VideoPositionPoint } from "types/";
import { AnalyticsBarChart, type BarDatasetConfig } from "./AnalyticsBarChart";
import { analyticsTheme } from "./analyticsTheme";
import {
  getTimestampSeries,
  buildTimestampChartOptions,
  type ChapterMilestone,
} from "./utils/";

const T_PREFIX = "media.projects.analytics.dashboard.summaryCards.seekPatterns";

export interface SeekPatternsChartProps {
  data: VideoPositionPoint[] | undefined;
  chapters?: ChapterMilestone[];
  maxSeconds?: number;
  loading: boolean;
}

export const SeekPatternsChart: React.FC<SeekPatternsChartProps> = ({
  data,
  chapters,
  maxSeconds,
  loading,
}) => {
  const t = useTranslate();
  const points = useMemo(() => data ?? [], [data]);

  const { xValues, labels } = useMemo(
    () => getTimestampSeries(points),
    [points]
  );

  // buildTimestampChartOptions is typed for "line"; the tooltip/annotation
  // shapes are chart-type-agnostic, so cast to the bar chart's options.
  const chartOptions = useMemo(
    () =>
      buildTimestampChartOptions({ chapters }) as Partial<ChartOptions<"bar">>,
    [chapters]
  );

  const datasets = useMemo<BarDatasetConfig[]>(
    () => [
      {
        label: t(`${T_PREFIX}.forwardSeeks`),
        data: points.map((p) => p.forward_seek_count),
        color: analyticsTheme.datasetBlue,
      },
      {
        label: t(`${T_PREFIX}.rewinds`),
        data: points.map((p) => p.rewind_count),
        color: analyticsTheme.datasetAmber,
      },
      {
        label: t(`${T_PREFIX}.inboundSeeks`),
        data: points.map((p) => p.inbound_seek_count),
        color: analyticsTheme.datasetGreen,
      },
    ],
    [points, t]
  );

  return (
    <AnalyticsBarChart
      title={t(`${T_PREFIX}.title`)}
      subtitle={t(`${T_PREFIX}.subtitle`)}
      labels={labels}
      datasets={datasets}
      xValues={xValues}
      xScaleType="linear"
      xMax={maxSeconds}
      loading={loading}
      height={200}
      direction="vertical"
      valueAxis="count"
      chartOptions={chartOptions}
    />
  );
};
